import React, { useState } from "react";
import { Alert, App, Card, ConfigProvider, Drawer, Grid, Layout, Pagination, theme } from "antd";
import authService from "../../services/auth.service";
import Loader from "../../components/Loader/Loader";
import { PAGE_SIZE, type ModalMode } from "./constants";
import { ADMIN_ROUTES } from "../../routes/config";
import { useAdminConsole } from "./hooks/useAdminConsole";
import { AdminHeader } from "./components/AdminHeader";
import { AdminSidebar } from "./components/AdminSidebar";
import { AdminToolbar } from "./components/AdminToolbar";
import { RecordModal } from "./components/RecordModal";
import { ProfileModal } from "./components/ProfileModal";
import "./AdminConsole.scss";
import { MainTable } from "./components/MainTable";
import { TrashTable } from "./components/TrashTable";

const { Content } = Layout;
const { useBreakpoint } = Grid;

interface AdminConsoleProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

const AdminConsole: React.FC<AdminConsoleProps> = ({ isDark, onToggleTheme }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const {
    form, profileForm,
    currentUser, meta, tab, page, search, loading, submitting,
    listData, trashData, selectedRecord, error, creatingNew,
    isModalOpen, profileOpen, profileLoading, profileSaving, profile,
    activeLabel, domainOptions, backendRoleOptions, trashRows,
    setTab, setPage, setSearch,
    openRecord, startCreate, closeModal, closeProfileModal,
    openProfileModal, handleProfileSave, handleProfileImageChange,
    refreshCurrentData, handleSave, handleSoftDelete, handleRestore, handlePermanentDelete,
  } = useAdminConsole();

  if (loading && !currentUser) {
    return <Loader message="Loading admin console..." />;
  }

  const menuItems = ADMIN_ROUTES.map(({ path, icon, label }) => ({ 
    key: path.slice(1), 
    icon, 
    label 
  }));

  const logout = async () => {
    await authService.logout();
    authService.clearAllData();
    window.location.href = "/login";
  };

  const sidebarContent = (
    <AdminSidebar
      selectedKey={tab}
      items={menuItems}
      onSelect={(key) => {
        setTab(key as typeof tab);
        if (isMobile) setIsMobileMenuOpen(false);
      }}
      isMobile={isMobile}
      currentUser={currentUser}
      profileLoading={profileLoading}
      onProfile={() => {
        void openProfileModal();
        if (isMobile) setIsMobileMenuOpen(false);
      }}
      onLogout={() => void logout()}
      isDark={isDark}
    />
  );

  return (
    <Layout className={`admin-console${isDark ? "" : " admin-console--light"}`}>
      <AdminHeader
        currentUser={currentUser}
        activeLabel={activeLabel}
        profileLoading={profileLoading}
        isDark={isDark}
        isMobile={isMobile}
        onToggleTheme={onToggleTheme}
        onProfile={() => void openProfileModal()}
        onLogout={() => void logout()}
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />

      <Layout className="admin-console__body">
        {isMobile ? (
          <Drawer
            placement="left"
            closable={false}
            onClose={() => setIsMobileMenuOpen(false)}
            open={isMobileMenuOpen}
            width={280}
            styles={{
              body: { padding: 0, background: isDark ? "#0b1424" : "#ffffff" },
              content: { background: isDark ? "#0b1424" : "#ffffff" },
            }}
            className="admin-console__drawer"
          >
            {sidebarContent}
          </Drawer>
        ) : (
          sidebarContent
        )}

        <Content className="admin-console__content">
          <Card variant="borderless" className="admin-console__content-card">
            <AdminToolbar
              search={search}
              activeLabel={activeLabel}
              showNew={tab !== "trash"}
              onSearchChange={(value) => { setSearch(value); setPage(1); }}
              onReset={() => void refreshCurrentData()}
              onNew={startCreate}
            />

            {error ? (
              <Alert type="error" showIcon message={error} className="admin-console__alert" />
            ) : null}

            {tab === "trash" ? (
              <TrashTable
                rows={trashRows}
                loading={loading}
                onRestore={(type, id) => void handleRestore(type, id)}
                onPermanentDelete={(type, id) => void handlePermanentDelete(type, id)}
              />
            ) : (
              <MainTable
                tab={tab}
                items={listData?.items || []}
                loading={loading}
                onEdit={(id, type) => void openRecord(id, type)}
                onDelete={(type, id) => void handleSoftDelete(type, id)}
              />
            )}

            <div className="admin-console__pagination">
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={
                  tab === "trash"
                    ? (trashData?.users.total || 0) +
                      (trashData?.admins.total || 0) +
                      (trashData?.domains.total || 0) +
                      (trashData?.roles.total || 0)
                    : listData?.total || 0
                }
                showSizeChanger={false}
                onChange={(nextPage) => setPage(nextPage)}
              />
            </div>
          </Card>
        </Content>
      </Layout>

      {tab !== "trash" && (
        <RecordModal
          open={isModalOpen}
          tab={tab as ModalMode}
          form={form}
          selectedRecord={selectedRecord}
          creatingNew={creatingNew}
          submitting={submitting}
          meta={meta}
          domainOptions={domainOptions}
          backendRoleOptions={backendRoleOptions}
          onCancel={closeModal}
          onOk={() => void handleSave()}
        />
      )}

      <ProfileModal
        open={profileOpen}
        form={profileForm}
        profile={profile}
        profileSaving={profileSaving}
        onCancel={closeProfileModal}
        onOk={() => void handleProfileSave()}
        onImageChange={handleProfileImageChange}
      />
    </Layout>
  );
};

const WrappedAdminConsole: React.FC = () => {
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem("ac-theme") !== "light",
  );

  const toggleTheme = () =>
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem("ac-theme", next ? "dark" : "light");
      return next;
    });

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: isDark
          ? {
              colorPrimary: "#1677ff",
              colorSuccess: "#2e7d3a",
              colorBgBase: "#081120",
              colorBgContainer: "#101a2e",
              colorBorder: "rgba(148, 163, 184, 0.18)",
              colorText: "#e2e8f0",
              colorTextSecondary: "#94a3b8",
              borderRadius: 6,
              fontFamily: "'Teachers', sans-serif",
            }
          : {
              colorPrimary: "#1677ff",
              colorSuccess: "#2e7d3a",
              borderRadius: 6,
              fontFamily: "'Teachers', sans-serif",
            },
      }}
    >
      <App>
        <AdminConsole isDark={isDark} onToggleTheme={toggleTheme} />
      </App>
    </ConfigProvider>
  );
};

export default WrappedAdminConsole;

